import { useCallback, useEffect, useRef, useState } from 'react';

export const MEDIA = '/assets/marketing/agency-r5';

export const useAgencyFilm = () => {
  const video = useRef(null);
  const [mobile, setMobile] = useState(() => window.matchMedia('(max-width: 600px)').matches);
  const [playing, setPlaying] = useState(false);
  const [started, setStarted] = useState(false);
  const [ended, setEnded] = useState(false);
  const [error, setError] = useState('');
  const [source, setSource] = useState('');
  const fallback = useRef(false);
  const sourceRef = useRef('');
  const previousStem = useRef('');
  const seenInView = useRef(false);
  const stem = `agency-r5${mobile ? '-mobile' : ''}`;
  const stemRef = useRef(stem); stemRef.current = stem;

  const load = useCallback((force = false, alternative = false) => {
    const v = video.current;
    if (!v) return;
    const mp4 = Boolean(v.canPlayType('video/mp4; codecs="avc1.640028, mp4a.40.2"'));
    const extension = alternative ? (mp4 ? 'webm' : 'mp4') : (mp4 ? 'mp4' : 'webm');
    const next = `${MEDIA}/${stemRef.current}.${extension}`;
    if (force || sourceRef.current !== next) {
      sourceRef.current = next; setSource(next); v.src = next; v.load();
    }
  }, []);
  const play = useCallback((restart = false) => {
    const v = video.current;
    if (!v) return;
    setError(''); seenInView.current = false; load();
    if (restart || v.ended) v.currentTime = 0;
    v.muted = false;
    v.play().then(() => {
      setStarted(true); setEnded(false);
      const bounds = v.getBoundingClientRect();
      seenInView.current = Math.min(bounds.bottom, window.innerHeight) - Math.max(bounds.top, 0) > bounds.height * .25;
    }).catch(exception => {
      if (exception.name !== 'AbortError') setError(exception.name === 'NotAllowedError' ? 'Press play to start the film.' : 'The film couldn’t play. Please try again.');
    });
  }, [load]);
  const retry = () => { fallback.current = false; setError(''); load(true); play(true); };
  const mediaError = () => {
    if (!fallback.current && sourceRef.current) {
      fallback.current = true; load(true, true);
      video.current?.play().catch(() => setError('The film couldn’t load. Retry or download the video.'));
    } else setError('The film couldn’t load. Retry or download the video.');
  };
  useEffect(() => {
    const query = window.matchMedia('(max-width: 600px)');
    const update = e => setMobile(e.matches);
    query.addEventListener('change', update);
    return () => query.removeEventListener('change', update);
  }, []);
  useEffect(() => {
    if (previousStem.current && previousStem.current !== stem) {
      video.current?.pause(); video.current?.removeAttribute('src'); video.current?.load();
      sourceRef.current = ''; setSource(''); setStarted(false); setEnded(false); setPlaying(false); setError(''); fallback.current = false;
    }
    previousStem.current = stem;
  }, [stem]);
  useEffect(() => {
    const v = video.current;
    const stop = () => { if (document.hidden) v?.pause(); };
    const hide = () => v?.pause();
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry.intersectionRatio >= .25) seenInView.current = true;
      else if (entry.intersectionRatio < .1 && seenInView.current) v?.pause();
    }, { threshold: [0, .1, .25] });
    if (v) observer.observe(v);
    document.addEventListener('visibilitychange', stop); window.addEventListener('pagehide', hide);
    return () => { v?.pause(); observer.disconnect(); document.removeEventListener('visibilitychange', stop); window.removeEventListener('pagehide', hide); };
  }, []);
  return { video, mobile, stem, source, playing, started, ended, error, play, retry, mediaError, setPlaying, setStarted, setEnded };
};